import React, { useEffect } from 'react';

import { MapTo } from '@adobe/aem-react-editable-components';
import { useQuery } from '@apollo/client';
import { TwoColumnsLayout } from 'Layouts/TwoColumnLayout/TwoColumnLayout';
import { GET_ARTICLES_OVERVIEW_DATA_QUERY, GET_EVENTS_OVERVIEW_DATA_QUERY } from 'Queries/Queries';
import { useAuth } from 'context/Auth';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';

import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { OverviewList } from 'components/OverviewLists/OverviewList';
import { PageTitleSection } from 'components/PageTitleSection/PageTitleSection';
import { SubscribeBox } from 'components/SubscribeBox/SubscribeBox';

import { OVERVIEW_LIST_TYPES, TENANT_ID } from 'global/constants';

import { getArticlesOverviewPath, getEventsOverviewPath } from 'utils/UrlUtil';
import { getTenantId, isBUofCurrentTenant } from 'utils/tenantUtil';

import './EventsAndArticlesOverview.scss';

const DATA = {
  [TENANT_ID.EDI]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Fachbeiträge und Veranstaltungen rund um Strom, Gas und übergreifende Themen.',
    subTitleArticles: 'Aktuelle Fachbeiträge',
    actionButtonArticles: 'Alle Fachbeiträge',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
  [TENANT_ID.SHNG]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Fachbeiträge und Veranstaltungen rund um Strom, Gas und übergreifende Themen.',
    subTitleArticles: 'Aktuelle Fachbeiträge',
    actionButtonArticles: 'Alle Fachbeiträge',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
  [TENANT_ID.AVA]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Nachrichten und Veranstaltungen zu technischen Anforderungen, dem Installateurportal, Besonderheiten im Netz der Avacon und aktuellen Themen.',
    subTitleArticles: 'Aktuelle Nachrichten',
    actionButtonArticles: 'Alle Nachrichten',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
  [TENANT_ID.BAG]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Nachrichten und Veranstaltungen zu technischen Anforderungen, dem Installateurportal, Besonderheiten im Netz der Bayernwerk und aktuellen Themen.',
    subTitleArticles: 'Aktuelle Nachrichten',
    actionButtonArticles: 'Alle Nachrichten',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
  [TENANT_ID.LVN]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Nachrichten und Veranstaltungen zu technischen Anforderungen, dem Installateurportal, Besonderheiten im Netz der LEW Verteilnetz und aktuellen Themen.',
    subTitleArticles: 'Aktuelle Nachrichten',
    actionButtonArticles: 'Alle Nachrichten',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
  [TENANT_ID.MTN]: {
    title: 'News & Veranstaltungen',
    subText:
      'Hier finden Sie alle Nachrichten und Veranstaltungen zu technischen Anforderungen, dem Installateurportal, Besonderheiten im Netz der MITNETZ STROM, MITNETZ GAS und Plauen NETZ und aktuellen Themen.',
    subTitleArticles: 'Aktuelle Nachrichten',
    actionButtonArticles: 'Alle Nachrichten',
    subTitleEvents: 'Aktuelle Veranstaltungen',
    actionButtonEvents: 'Alle Veranstaltungen',
  },
};

const EventsAndArticlesOverview = () => {
  const { installer, isLoading: isInstallerLoading } = useCurrentInstaller();
  const { isCIAM } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const CONTENT = DATA[getTenantId()];

  const { loading: articlesLoading, data: articlesData } = useQuery(
    GET_ARTICLES_OVERVIEW_DATA_QUERY,
    {
      fetchPolicy: 'network-only',
    }
  );

  const { loading: eventsLoading, data: eventData } = useQuery(GET_EVENTS_OVERVIEW_DATA_QUERY, {
    fetchPolicy: 'network-only',
  });
  const leftColumnContent = (
    <>
      <PageTitleSection title={CONTENT.title} subText={CONTENT.subText} notFramed />

      <OverviewList
        className="events-and-articles-overview__list"
        headline={CONTENT.subTitleArticles}
        listData={articlesData?.articles}
        isLoading={articlesLoading}
        listType={OVERVIEW_LIST_TYPES.article}
        buttonLabel={CONTENT.actionButtonArticles}
        buttonLink={getArticlesOverviewPath()}
      />
      <OverviewList
        className="events-and-articles-overview__list"
        headline={CONTENT.subTitleEvents}
        listData={eventData?.events}
        isLoading={eventsLoading}
        buttonLabel={CONTENT.actionButtonEvents}
        buttonLink={getEventsOverviewPath()}
      />
    </>
  );

  // articles and events loading, when CIAM we have to wait for installer data too
  if (articlesLoading || eventsLoading || isInstallerLoading || (isCIAM && !installer)) {
    return <LoadingIndicator />;
  }
  const rightColumnContent = (!isCIAM || isBUofCurrentTenant(installer.pflegendeBu)) && (
    <SubscribeBox isCIAM={isCIAM} />
  );

  return <TwoColumnsLayout leftColumn={leftColumnContent} rightColumn={rightColumnContent} />;
};

MapTo('e-fix/components/content/newsandeventsoverview')(EventsAndArticlesOverview);
